import type { FC } from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import classNames from "classnames";

import { type PrizeLocatorFilterItem } from "@des/locators/components/PrizeLocator";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";
import { toast } from "@jti/core-components/components/Toaster";
import { useLoyaltyListRewardFilters, useLoyaltyPrizes } from "@jti/rest-api";
import { type GetPrizesElementResponseType, type RewardFilterType } from "@jti/rest-api-types";
import { useSelector } from "@jti/store/reduxHooks";
import { selectIsUserDataFetching } from "@jti/store/selectors/selectIsUserDataFetching";
import { type RootStoreState } from "@jti/store/types";
import { CategoryFilter } from "@jti/ui/components/CategoryFilter";
import { DEPRECATED_Loading } from "@jti/ui/components/DEPRECATED_Loading";
import { LoadMore } from "@jti/ui/components/LoadMore";
import { Typography } from "@jti/ui/components/Typography";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import useLoggedUser from "../../hooks/useLoggedUser";
import { RedeemDialogV2 } from "../RedeemDialogV2";
import type { TrustedPlacesV2Props } from "../TrustedPlacesV2/TrustedPlacesV2";
import { TrustedPlacesV2 } from "../TrustedPlacesV2/TrustedPlacesV2";

import ItemsList from "./ItemsList/ItemsList";
import type { PointsRangeValue } from "./SimplifiedPrizesFilters/PointsRange";
import type { SortByValue } from "./SimplifiedPrizesFilters/SortBy";
import {
  getCategoryTotals,
  getDisabledCategoryKeysFromTotals,
  mapRewardsFiltersToSubcategoryOptions,
  type SubcategoryTranslation,
} from "./util/mapRewardsFilters";
import {
  getActiveFiltersCount,
  getPointsRangeQuery,
  getSortByQuery,
  getSubcategoriesQuery,
} from "./util/simplifedPrizesUtils";

import type { SimplifiedPrizesFiltersProps } from "./SimplifiedPrizesFilters";
import { SimplifiedPrizesFilters } from "./SimplifiedPrizesFilters";

import styles from "./SimplifiedPrizes.module.scss";

type SimplifiedPrizesTeaserDataType = {
  teaserTitle: string;
  teaserLinkUrl: string;
  teaserLinkText: string;
};

type FilterData = {
  label: string;
  iconUrl: string;
  isDisabled: boolean;
};

type SimplifiedPrizesAdditionalFiltersData = Omit<
  SimplifiedPrizesFiltersProps,
  | "activeFiltersCount"
  | "onAvailableForMeChange"
  | "onSortByChange"
  | "onCategoryChange"
  | "onSubcategoryIdsChange"
  | "onApplyFilters"
  | "onResetFilters"
  | "onCancelFilters"
  | "onPointsRangeChange"
> & {
  subcategoryTranslations?: readonly SubcategoryTranslation[];
};

export type SimplifiedPrizesV2Props = {
  discoverUrlParent?: string;
  discoverButtonText?: string;
  ctaLabel: string;
  color?: "white" | "black";
  noOffersText: string;
  noOffersSubtext?: string;
  newOfferDays?: number;
  newLabel: string;
  pointsLabel?: string;
  oneDayLabel?: string;
  daysLabel?: string;
  isTeaser: boolean;
  pagination: number;
  teaserData: SimplifiedPrizesTeaserDataType;
  loadMoreButtonLabel: string;
  errorMessage?: string;
  outOfStockLabel: string;
  earnPointsCtaText?: string;
  earnPointsCtaUrl?: string;
  isInEditor?: boolean;
  logoUrl?: string;
  logoAlt?: string;
  categoryFilterTitle?: string;
  prizeCardSizeVariant?: "default" | "small";
  prizeCardVariant?: "default" | "blurredBackground";
  isLotteryAddressRequirementDisabled?: boolean;
  // filtersData
  isFiltersEnabled?: boolean;
  allFiltersData?: FilterData;
  virtualFiltersData?: FilterData;
  physicalFiltersData?: FilterData;
  lotteryFiltersData?: FilterData;
  sensorialFiltersData?: FilterData;
  mapCategoryFilterTitle?: string;
  isAdditionalFiltersEnabled?: boolean;
  additionalFiltersData?: SimplifiedPrizesAdditionalFiltersData;
} & Omit<TrustedPlacesV2Props, "categoryFilterTitle">;

export type SimplifiedPrizesFilter = Pick<
  PrizeLocatorFilterItem,
  "isActiveFilter" | "iconUrl" | "label" | "isDisabled"
> & {
  categoryKey: RewardFilterType;
};

type AdditionalPrizesFilters = {
  availableForMe: boolean;
  clubPloomTiers: string[];
  pointsRangeValue?: PointsRangeValue;
  sortByValue?: SortByValue;
  selectedCategoryId?: string;
  selectedSubcategoryIds: string[];
};

export type SimplifiedPrizesActiveFilters = {
  category: SimplifiedPrizesFilter;
} & AdditionalPrizesFilters;

const teaserFallbackData = { teaserTitle: "", teaserLinkUrl: "", teaserLinkText: "" };

export const SimplifiedPrizesV2: FC<SimplifiedPrizesV2Props> = (props) => {
  const {
    ctaLabel = "Redeem now",
    color = "white",
    noOffersText,
    noOffersSubtext,
    newOfferDays,
    newLabel = "New",
    oneDayLabel,
    pointsLabel,
    daysLabel,
    isTeaser,
    teaserData = teaserFallbackData,
    pagination = 3,
    loadMoreButtonLabel = "Load more",
    errorMessage = "Something went wrong. Please try again later.",
    outOfStockLabel = "Out of stock",
    earnPointsCtaText,
    earnPointsCtaUrl,
    discoverUrlParent,
    discoverButtonText,
    isInEditor = false,
    logoUrl,
    logoAlt,
    prizeCardSizeVariant = "default",
    prizeCardVariant = "default",
    isLotteryAddressRequirementDisabled = false,
    // Filters Props
    isFiltersEnabled = false,
    categoryFilterTitle,
    allFiltersData = { iconUrl: "", label: "All", isDisabled: false },
    virtualFiltersData = { iconUrl: "", label: "Virtual", isDisabled: false },
    physicalFiltersData = { iconUrl: "", label: "Physical", isDisabled: false },
    lotteryFiltersData = { iconUrl: "", label: "Lottery", isDisabled: false },
    sensorialFiltersData = { iconUrl: "", label: "Sensorial", isDisabled: false },
    mapCategoryFilterTitle,
    quantityAriaLabel,
    increaseAriaLabel,
    decreaseAriaLabel,
    isAdditionalFiltersEnabled = false,
    additionalFiltersData,
  } = props;
  const [dialogData, setDialogData] = useState<GetPrizesElementResponseType | Record<string, never>>({});
  const { __ } = useTranslations();

  const tier = useSelector((state: RootStoreState) => state.loyaltyCore.tier);
  const {
    isLoaded,
    uuid,
    points: { balance },
  } = useSelector((state: RootStoreState) => state.loyaltyCore);
  const isUserDataFetching = useSelector(selectIsUserDataFetching);
  const [isLogged] = useLoggedUser();
  const { teaserTitle, teaserLinkUrl, teaserLinkText } = teaserData;

  const filtersData: SimplifiedPrizesFilter[] = useMemo(
    () => [
      { categoryKey: "all", isActiveFilter: false, ...allFiltersData },
      { categoryKey: "lottery", isActiveFilter: false, ...lotteryFiltersData },
      { categoryKey: "virtual", isActiveFilter: false, ...virtualFiltersData },
      { categoryKey: "physical", isActiveFilter: false, ...physicalFiltersData },
      { categoryKey: "sensorial", isActiveFilter: false, ...sensorialFiltersData },
    ],
    [allFiltersData, lotteryFiltersData, physicalFiltersData, sensorialFiltersData, virtualFiltersData]
  );

  const { data: rewardsFilters } = useLoyaltyListRewardFilters({
    enabled: isFiltersEnabled && isAdditionalFiltersEnabled && !isInEditor,
  });

  const disabledCategoryKeys = getDisabledCategoryKeysFromTotals(rewardsFilters);
  const resolvedFiltersData: SimplifiedPrizesFilter[] = filtersData.map((filter) => ({
    ...filter,
    isDisabled: filter.isDisabled || disabledCategoryKeys.has(filter.categoryKey),
  }));

  const subcategoryOptions =
    additionalFiltersData?.subcategoryOptions ??
    mapRewardsFiltersToSubcategoryOptions(rewardsFilters, additionalFiltersData?.subcategoryTranslations);

  const categoryTotals = getCategoryTotals(rewardsFilters);
  const syncedModalCategoryOptions = resolvedFiltersData
    .filter((filter) => filter.categoryKey !== "all" && !filter.isDisabled)
    .map((filter) => {
      const total = categoryTotals.get(filter.categoryKey);
      const label = total === undefined ? filter.label : `${filter.label} (${total})`;

      return {
        id: filter.categoryKey,
        label,
        disabled: filter.isDisabled,
      };
    });

  const DEFAULT_ADDITIONAL_FILTERS: AdditionalPrizesFilters = {
    availableForMe: false,
    clubPloomTiers: [],
    sortByValue: additionalFiltersData?.sortByFilterItems?.[0]?.value,
    selectedCategoryId: undefined,
    selectedSubcategoryIds: [],
    pointsRangeValue: "all",
  };
  const [activeFilters, setActiveFilters] = useState<SimplifiedPrizesActiveFilters>({
    category: filtersData[0],
    ...DEFAULT_ADDITIONAL_FILTERS,
  });
  const [draftAdditionalFilters, setDraftAdditionalFilters] =
    useState<AdditionalPrizesFilters>(DEFAULT_ADDITIONAL_FILTERS);

  const appliedAdditionalFiltersRef = useRef<AdditionalPrizesFilters>(DEFAULT_ADDITIONAL_FILTERS);

  const subcategoryParentById = new Map(
    subcategoryOptions?.map((subcategory) => [subcategory.id, subcategory.parentCategoryId])
  );

  const sanitizeSubcategoryIds = (subcategoryIds: string[], categoryId?: string) => {
    if (!categoryId || !subcategoryIds.length) return subcategoryIds;

    return subcategoryIds.filter((subcategoryId) => subcategoryParentById.get(subcategoryId) === categoryId);
  };

  const normalizeModalCategoryId = (categoryId?: string) => {
    if (!categoryId || categoryId === "all") return undefined;
    return categoryId;
  };

  const getFilterByCategoryKey = (categoryKey?: string) =>
    filtersData.find((filter) => filter.categoryKey === categoryKey) ??
    filtersData.find((filter) => filter.categoryKey === "all");

  // Single source of truth for category selection for CategoryFilter and the modal filters
  const applyCategorySelection = (categoryKey?: string) => {
    const normalizedCategoryId = normalizeModalCategoryId(categoryKey);
    const nextCategoryFilter = getFilterByCategoryKey(categoryKey);
    const withCategory = (subcategoryIds: string[]) => ({
      selectedCategoryId: normalizedCategoryId,
      selectedSubcategoryIds: sanitizeSubcategoryIds(subcategoryIds, normalizedCategoryId),
    });

    appliedAdditionalFiltersRef.current = {
      ...appliedAdditionalFiltersRef.current,
      ...withCategory(appliedAdditionalFiltersRef.current.selectedSubcategoryIds),
    };

    setActiveFilters((prev) => ({
      ...prev,
      category: nextCategoryFilter ?? prev.category,
      ...withCategory(prev.selectedSubcategoryIds),
    }));

    setDraftAdditionalFilters((prev) => ({ ...prev, ...withCategory(prev.selectedSubcategoryIds) }));
  };

  const handleSelectFilter = (filterKey: string) => applyCategorySelection(filterKey);

  const handleAvailableForMeChange = (checked: boolean) =>
    setDraftAdditionalFilters((prev) => ({ ...prev, availableForMe: checked }));

  const handleClubPloomTiersChange = (tiers: string[]) =>
    setDraftAdditionalFilters((prev) => ({ ...prev, clubPloomTiers: tiers }));

  const handleSortByChange = (sortByValue: SortByValue) =>
    setDraftAdditionalFilters((prev) => ({ ...prev, sortByValue }));

  const handleCategoryChange = (categoryId: string) =>
    setDraftAdditionalFilters((prev) => {
      const isDeselecting = prev.selectedCategoryId === categoryId;
      const nextCategoryId = isDeselecting ? undefined : categoryId;

      return {
        ...prev,
        selectedCategoryId: nextCategoryId,
        selectedSubcategoryIds: isDeselecting
          ? prev.selectedSubcategoryIds.filter((id) => subcategoryParentById.get(id) !== categoryId)
          : sanitizeSubcategoryIds(prev.selectedSubcategoryIds, categoryId),
      };
    });

  const handleSubcategoryIdsChange = (subcategoryIds: string[]) =>
    setDraftAdditionalFilters((prev) => ({ ...prev, selectedSubcategoryIds: subcategoryIds }));

  const handlePointsRangeChange = (pointsRangeValue: PointsRangeValue) =>
    setDraftAdditionalFilters((prev) => ({ ...prev, pointsRangeValue }));

  const handleApplyAdditionalFilters = () => {
    const normalizedSelectedCategoryId = normalizeModalCategoryId(draftAdditionalFilters.selectedCategoryId);

    const sanitizedSubcategoryIds = sanitizeSubcategoryIds(
      draftAdditionalFilters.selectedSubcategoryIds,
      normalizedSelectedCategoryId
    );

    const nextAdditionalFilters: AdditionalPrizesFilters = {
      ...draftAdditionalFilters,
      selectedCategoryId: normalizedSelectedCategoryId,
      selectedSubcategoryIds: sanitizedSubcategoryIds,
    };

    appliedAdditionalFiltersRef.current = nextAdditionalFilters;

    const selectedCategoryFilter = getFilterByCategoryKey(nextAdditionalFilters.selectedCategoryId);

    setActiveFilters((prev) => ({
      ...prev,
      ...nextAdditionalFilters,
      category: selectedCategoryFilter ?? prev.category,
    }));

    setDraftAdditionalFilters(nextAdditionalFilters);
  };

  const handleResetAdditionalFilters = () => setDraftAdditionalFilters(DEFAULT_ADDITIONAL_FILTERS);

  const handleDiscardAdditionalFilters = () => setDraftAdditionalFilters(appliedAdditionalFiltersRef.current);

  const { orderBy, sort } = getSortByQuery(activeFilters.sortByValue);
  // "Available for me" disables the points range and Club Ploom tiers filters, so their values must not be sent to BE.
  const { minPoints, maxPoints } = activeFilters.availableForMe
    ? {}
    : getPointsRangeQuery(activeFilters.pointsRangeValue);
  const levels =
    !activeFilters.availableForMe && activeFilters.clubPloomTiers.length > 0 ? activeFilters.clubPloomTiers : undefined;

  const subcategories = getSubcategoriesQuery(activeFilters.selectedSubcategoryIds);

  const activeFiltersCount = getActiveFiltersCount(activeFilters);

  const { prizes, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage, refetchAllPages } =
    useLoyaltyPrizes({
      perPage: pagination,
      isTeaser,
      enabled: isLoaded && activeFilters.category.categoryKey !== "sensorial",
      consumerUuid: uuid || undefined,
      categoryKey: activeFilters.category.categoryKey,
      rewardType: "prize",
      orderBy,
      sort,
      availableForMe: activeFilters.availableForMe,
      minPoints,
      maxPoints,
      levels,
      subcategories,
    });
  const resetDialogData = () => setDialogData({});

  const handleLoadMoreClick = useCallback(async () => {
    const res = await fetchNextPage();
    if (res.status === "error" || res.isError) {
      console.error(res.error);
      toast.error(__("mobile.error.something_went_wrong"));
    }
  }, [fetchNextPage, __]);

  const loadMoreButton = useMemo(() => {
    if (isTeaser || !hasNextPage) return null;
    return isFetchingNextPage ? (
      <DEPRECATED_Loading dataTestId="loadMoreLoading" />
    ) : (
      <LoadMore label={loadMoreButtonLabel} data-testid="simplifiedPrizesLoadMore" onClick={handleLoadMoreClick} />
    );
  }, [isTeaser, hasNextPage, isFetchingNextPage, loadMoreButtonLabel, handleLoadMoreClick]);

  if (isError) {
    return (
      <Typography variant="h2" weight="light" className={styles.error}>
        {errorMessage}
      </Typography>
    );
  }

  const isReady = !isLoading && !isUserDataFetching;

  const PrizeComponent = () =>
    activeFilters.category.categoryKey === "sensorial" ? (
      <TrustedPlacesV2 {...props} categoryFilterTitle={mapCategoryFilterTitle} />
    ) : (
      <div className={styles.listWrapper}>
        <ItemsList
          discoverButtonText={discoverButtonText}
          discoverUrlParent={discoverUrlParent}
          items={prizes}
          pointsLabel={pointsLabel ?? ""}
          ctaLabel={ctaLabel}
          color={color}
          noOffersText={noOffersText}
          noOffersSubtext={noOffersSubtext}
          points={balance}
          setDialogData={setDialogData}
          newBadgeData={{ newOfferDays, label: newLabel }}
          limitedOfferLabels={{ daysLabel, oneDayLabel }}
          tier={tier.name}
          outOfStockLabel={outOfStockLabel}
          isLogged={isLogged}
          earnPointsCtaText={earnPointsCtaText}
          earnPointsCtaUrl={earnPointsCtaUrl}
          prizeCardSizeVariant={prizeCardSizeVariant}
          prizeCardVariant={prizeCardVariant}
        />

        <div className={styles.button}>{loadMoreButton}</div>

        {isTeaser && (
          <Button
            variant="text-muted"
            size="sm"
            className={styles.link}
            href={teaserLinkUrl}
            iconAfter={LongArrowForwardIcon}
          >
            {teaserLinkText}
          </Button>
        )}
      </div>
    );

  return (
    <div
      className={classNames(styles.simplifiedPrizes, {
        [styles.teaser]: isTeaser,
      })}
    >
      {isTeaser && (
        <Typography variant="body2" weight="light" className={styles.title}>
          {teaserTitle}
        </Typography>
      )}

      {isFiltersEnabled && (
        <CategoryFilter
          onFilterClick={handleSelectFilter}
          activeFilters={[activeFilters.category]}
          filters={resolvedFiltersData}
          categoryFilterTitle={categoryFilterTitle}
          additionalFiltersSlot={
            isAdditionalFiltersEnabled &&
            additionalFiltersData && (
              <SimplifiedPrizesFilters
                {...additionalFiltersData}
                activeFiltersCount={activeFiltersCount}
                categoryOptions={syncedModalCategoryOptions}
                subcategoryOptions={subcategoryOptions}
                isCategoryFilterEnabled={additionalFiltersData.isCategoryFilterEnabled}
                isSubcategoryFilterEnabled={additionalFiltersData.isSubcategoryFilterEnabled}
                isAvailableForMeFilterEnabled={isLogged && additionalFiltersData.isAvailableForMeFilterEnabled}
                availableForMeFilterLabel={isLogged ? additionalFiltersData.availableForMeFilterLabel : undefined}
                isAvailableForMeChecked={draftAdditionalFilters.availableForMe}
                onAvailableForMeChange={handleAvailableForMeChange}
                selectedClubPloomTiers={draftAdditionalFilters.clubPloomTiers}
                onClubPloomTiersChange={handleClubPloomTiersChange}
                isSortByFilterEnabled={additionalFiltersData.isSortByFilterEnabled}
                sortByValue={draftAdditionalFilters.sortByValue}
                pointsRangeValue={draftAdditionalFilters.pointsRangeValue}
                onSortByChange={handleSortByChange}
                selectedCategoryId={draftAdditionalFilters.selectedCategoryId}
                onCategoryChange={handleCategoryChange}
                selectedSubcategoryIds={draftAdditionalFilters.selectedSubcategoryIds}
                onSubcategoryIdsChange={handleSubcategoryIdsChange}
                onPointsRangeChange={handlePointsRangeChange}
                onApplyFilters={handleApplyAdditionalFilters}
                onResetFilters={handleResetAdditionalFilters}
                onCancelFilters={handleDiscardAdditionalFilters}
              />
            )
          }
        />
      )}
      {isReady ? <PrizeComponent /> : <DEPRECATED_Loading dataTestId="prizesLoading" className={styles.loader} />}

      <RedeemDialogV2
        pointsBalance={balance}
        prizeData={dialogData}
        logoAlt={logoAlt}
        logoUrl={logoUrl}
        variant={prizeCardVariant}
        isInEditor={isInEditor}
        isDialogOpen={!!dialogData?.prize_uuid}
        onCloseDialog={resetDialogData}
        handlePrizePurchase={refetchAllPages}
        quantityAriaLabel={quantityAriaLabel}
        increaseAriaLabel={increaseAriaLabel}
        decreaseAriaLabel={decreaseAriaLabel}
        isLotteryAddressRequirementDisabled={isLotteryAddressRequirementDisabled}
      />
    </div>
  );
};
